(() => {
  if (window.WolfColors) return;
  const catalogCache = new Map();
  const normalizeColor = (value) => String(value || '').normalize('NFKC').trim().replace(/\s+/g, ' ').toLowerCase();
  const root = () => window.Shopify?.routes?.root || '/';

  // The variant metafield selects a presentation color, never a product/glaze match.
  const brandPalette = Object.freeze({
    Blue: '#517efe', Red: '#f46c53', Yellow: '#fece90', Black: '#2f2f2f', White: '#e4ebf0',
  });

  function inferBrandColor(color) {
    const name = normalizeColor(color);
    const known = {
      talia: 'Red', tucker: 'Black', juno: 'Black', mar: 'Blue', anniversary: 'Blue',
      'black madrona': 'Black', 'turquoise moonrise': 'Yellow', 'blue stripe with black': 'Black',
      plume: 'Red', confetti: 'White', dipper: 'Yellow',
    };
    if (known[name]) return known[name];
    if (/cerulean|tabor|blue|cobalt/.test(name)) return 'Blue';
    if (/red|poppy|harvest|tiger|ember|coral/.test(name)) return 'Red';
    if (/sunrise|yellow|peach/.test(name)) return 'Yellow';
    if (/white|ivory|cream|snow|salt/.test(name)) return 'White';
    // Retired green glazes and unrecognized names use the neutral brand color.
    return 'Black';
  }

  function colorStyle(color, _nativeSwatch, brandColor) {
    const selected = Object.keys(brandPalette).find((name) => normalizeColor(name) === normalizeColor(brandColor)) || inferBrandColor(color);
    return {
      background: brandPalette[selected],
      foreground: selected === 'Yellow' || selected === 'White' ? '#2f2f2f' : '#ffffff',
      brandColor: selected,
    };
  }

  function selectedVariant(product, section, initialVariantId) {
    const variants = product.variants || [];
    const picker = section?.querySelector('variant-radios, variant-selects');
    const controls = [...(picker?.querySelectorAll('input[type="radio"]:checked, select') || [])];
    // Read the controls themselves so a delayed Shopify response cannot select an older color.
    if (controls.length) {
      const exact = variants.find((variant) => variant.options?.length === controls.length &&
        variant.options.every((value, index) => normalizeColor(value) === normalizeColor(controls[index].value)));
      if (exact) return exact;
      const colorControl = controls.find((input) => /^(?:options\[)?(?:colou?r|glaze|colou?rway)\]?$/i.test(input.name));
      if (colorControl) {
        const colorMatch = variants.find((variant) => normalizeColor(variant.color) === normalizeColor(colorControl.value));
        if (colorMatch) return colorMatch;
      }
    }
    const id = picker?.currentVariant?.id || section?.querySelector('form[action*="/cart/add"] input[name="id"]')?.value || initialVariantId;
    return variants.find((variant) => String(variant.id) === String(id)) || variants.find((variant) => variant.available) || variants[0];
  }

  function shopColorUrl(color, collectionUrl) {
    const url = new URL(collectionUrl || `${root()}collections/shop-all`, location.origin);
    url.searchParams.delete('page');
    url.searchParams.set('wc_color', color);
    return url.pathname + url.search;
  }

  async function fetchCatalog(collectionUrl) {
    let url = new URL(collectionUrl || `${root()}collections/all`, location.origin);
    if (url.origin !== location.origin) throw new Error('The color catalog must be on this storefront.');
    url.searchParams.delete('page');
    url.searchParams.delete('wc_color');
    url.searchParams.delete('wc_form');
    url.searchParams.set('section_id', 'wolf-color-catalog');
    const products = new Map();
    const seen = new Set();
    while (url) {
      if (seen.has(url.href)) throw new Error('The color catalog returned a repeated page.');
      seen.add(url.href);
      const response = await fetch(url.href, { signal: AbortSignal.timeout(20000) });
      if (!response.ok) throw new Error(`Color catalog unavailable (${response.status}).`);
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const data = html.querySelector('[data-wolf-color-catalog]');
      if (!data) throw new Error('Color catalog data is missing.');
      const page = JSON.parse(data.textContent);
      if (!Array.isArray(page.products)) throw new Error('Invalid color catalog.');
      page.products.forEach((product) => products.set(String(product.id), product));
      if (page.next) {
        const next = new URL(page.next, url);
        if (next.origin !== location.origin) throw new Error('Invalid color catalog page.');
        // Some Shopify pagination URLs omit Section Rendering API parameters.
        for (const [key, value] of url.searchParams) {
          if (key !== 'page' && !next.searchParams.has(key)) next.searchParams.append(key, value);
        }
        next.searchParams.set('section_id', 'wolf-color-catalog');
        url = next;
      } else url = null;
    }
    return [...products.values()];
  }

  function loadCatalog(collectionUrl) {
    const key = collectionUrl || `${root()}collections/all`;
    if (!catalogCache.has(key)) {
      catalogCache.set(key, fetchCatalog(key).catch((error) => {
        catalogCache.delete(key);
        throw error;
      }));
    }
    return catalogCache.get(key);
  }

  function matchingProducts(products, color, { excludeProductIds = [], limit = 12 } = {}) {
    if (!normalizeColor(color)) return [];
    const excluded = new Set(excludeProductIds.map(String));
    const matches = [];
    for (const product of products) {
      if (excluded.has(String(product.id))) continue;
      const variant = product.variants.find((item) => item.available && item.image && normalizeColor(item.color) === normalizeColor(color));
      if (variant) matches.push({ product, variant });
      if (matches.length >= limit) break;
    }
    return matches;
  }

  function renderCard({ product, variant }) {
    const card = document.createElement('li');
    card.className = 'wolf-color-card';
    const link = document.createElement('a');
    link.href = variant.url;
    link.className = 'wolf-color-card__link';
    link.setAttribute('aria-label', `${product.title}${variant.color ? ` in ${variant.color}` : ''}, ${variant.priceText}`);
    const media = document.createElement('div');
    media.className = 'wolf-color-card__media';
    if (variant.image) {
      const image = document.createElement('img');
      image.src = variant.image;
      image.alt = `${product.title}${variant.color ? ` — ${variant.color}` : ''}`;
      image.loading = 'lazy';
      image.width = 960;
      image.height = 960;
      media.append(image);
    }
    const details = document.createElement('div');
    details.className = 'wolf-color-card__details';
    const title = document.createElement('span');
    title.className = 'wolf-color-card__title';
    title.textContent = product.title;
    const price = document.createElement('span');
    price.className = 'wolf-color-card__price';
    price.textContent = variant.priceText;
    details.append(title, price);
    link.append(media, details);
    card.append(link);
    return card;
  }

  function setupCarousel(element) {
    const track = element.querySelector('.wolf-color-track');
    const previous = element.querySelector('[data-wolf-previous]');
    const next = element.querySelector('[data-wolf-next]');
    if (!track || !previous || !next) return () => {};
    const controller = new AbortController();
    const pagination = element.querySelector('[data-wolf-pagination]');
    let positions = [];
    let dots = [];
    let active = 0;
    const update = () => {
      const maximum = track.scrollWidth - track.clientWidth;
      previous.disabled = track.scrollLeft <= 2;
      next.disabled = track.scrollLeft >= maximum - 2;
      previous.parentElement.hidden = maximum <= 2;
      active = positions.reduce((closest, position, index) =>
        Math.abs(position - track.scrollLeft) < Math.abs(positions[closest] - track.scrollLeft) ? index : closest, 0);
      dots.forEach((dot, index) => dot.setAttribute('aria-current', String(index === active)));
    };
    const goTo = (index) => {
      track.scrollTo({ left: positions[Math.max(0, Math.min(index, positions.length - 1))] || 0, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
    };
    const measure = () => {
      const maximum = Math.max(0, track.scrollWidth - track.clientWidth);
      const inset = parseFloat(getComputedStyle(track).paddingLeft) || 0;
      const origin = track.getBoundingClientRect().left + inset;
      positions = [0];
      for (const card of track.children) {
        const position = Math.min(maximum, Math.max(0, card.getBoundingClientRect().left - origin + track.scrollLeft));
        if (position - positions[positions.length - 1] > 2) positions.push(position);
      }
      if (maximum - positions[positions.length - 1] > 2) positions.push(maximum);
      if (pagination) {
        pagination.hidden = maximum <= 2;
        if (dots.length !== positions.length) {
          dots = positions.map((_, index) => {
            const dot = document.createElement('button');
            dot.type = 'button';
            dot.className = 'same-color-products__dot';
            dot.setAttribute('aria-label', `Go to slide ${index + 1} of ${positions.length}`);
            dot.setAttribute('aria-controls', track.id);
            dot.dataset.slide = index;
            return dot;
          });
          pagination.replaceChildren(...dots);
        }
      }
      update();
    };
    previous.addEventListener('click', () => goTo(active - 1), { signal: controller.signal });
    next.addEventListener('click', () => goTo(active + 1), { signal: controller.signal });
    pagination?.addEventListener('click', (event) => {
      const dot = event.target.closest('[data-slide]');
      if (dot) goTo(Number(dot.dataset.slide));
    }, { signal: controller.signal });
    track.addEventListener('scroll', update, { passive: true, signal: controller.signal });
    const observer = new ResizeObserver(measure);
    observer.observe(track);
    if (track.firstElementChild) observer.observe(track.firstElementChild);
    const frame = requestAnimationFrame(measure);
    return () => { controller.abort(); observer.disconnect(); cancelAnimationFrame(frame); };
  }

  window.WolfColors = { normalizeColor, brandPalette, inferBrandColor, colorStyle, selectedVariant, loadCatalog, matchingProducts, renderCard, shopColorUrl, setupCarousel };
})();
